/**
 * Real-yield / inflation math — single source of truth.
 *
 * Jul 7, 2026. Previously every consumer around the codebase used a
 * simple linear approximation `nominal - cpi`, which is only accurate
 * when both rates are small. At higher yields or higher inflation
 * (e.g. BDCs at 11% nominal, CPI at 4%) the error becomes financially
 * meaningful — 0.15pp over the life of a position compounds into real
 * dollars.
 *
 * This module standardises on the multiplicative Fisher equation:
 *
 *     real = (1 + nominal) / (1 + inflation) - 1
 *
 * All call sites accept and return PERCENT values (e.g. 4.2, not 0.042)
 * because that's what the rest of the app already trades in.
 *
 * References:
 *   Fisher, Irving. "The Theory of Interest" (1930) — chapter 2 on
 *   the exact relationship between nominal and real rates.
 */

/**
 * Fisher-exact real yield in percent.
 *   nominalPct — annualised nominal yield (e.g. 4.2)
 *   cpiPct     — inflation rate over the same period (e.g. 3.1)
 * Returns null when either input is not a finite number.
 *
 * Example: computeRealYield(4.2, 3.1) → 1.0669 (not the linear 1.10).
 */
export function computeRealYield(nominalPct, cpiPct) {
  if (typeof nominalPct !== 'number' || !Number.isFinite(nominalPct)) return null;
  if (typeof cpiPct !== 'number' || !Number.isFinite(cpiPct)) return null;
  const n = nominalPct / 100;
  const c = cpiPct / 100;
  // Fisher — see file header.
  return ((1 + n) / (1 + c) - 1) * 100;
}

/**
 * Cumulative compound inflation factor over `years` at `annualCpiPct`.
 * Returns a decimal multiplier (e.g. 1.343 for 3% × 10 years).
 */
export function cumulativeInflationFactor(annualCpiPct, years) {
  if (typeof annualCpiPct !== 'number' || !Number.isFinite(annualCpiPct)) return null;
  if (typeof years !== 'number' || !Number.isFinite(years) || years < 0) return null;
  return Math.pow(1 + annualCpiPct / 100, years);
}

/**
 * Convert a nominal future dollar amount into present-day
 * purchasing-power dollars using compound CPI discounting.
 */
export function realDollarsToday(nominalFutureUsd, annualCpiPct, years) {
  const factor = cumulativeInflationFactor(annualCpiPct, years);
  if (factor == null || typeof nominalFutureUsd !== 'number') return null;
  return nominalFutureUsd / factor;
}
